(function(){
'use strict';

// Pure policy. It describes a cart transaction; it never performs cart I/O.
const KIT = '9953072251193';
const SET = '10191250194745';
const TRIAL_PLAN = '692131856697';
const DEFAULTS = Object.freeze({
  marketCountry: 'US', currency: 'USD', offerCents: 1495,
  source: 'trial-mini-cart-20-v1', experiment: 'trial-mini-cart-20-v1',
  shippingCode: 'TRIAL-EXTRA-SHIP', offerCode: 'TRIAL-EXTRA1495',
  freeCode: 'HXK2RD8B3XCA',
});
const id = value => String(value == null ? '' : value);
const planId = line => id(line?.selling_plan_allocation?.selling_plan?.id || line?.selling_plan);
const productId = line => id(line?.product_id);
const variantId = line => id(line?.variant_id || line?.id);
const quantity = line => Number.isInteger(line?.quantity) && line.quantity > 0 ? line.quantity : 0;
const config = options => ({...DEFAULTS, ...options});
const owned = (line, o) => productId(line) === SET &&
  line.properties?._sl_trial_mini_source === o.source &&
  line.properties?._sl_trial_mini_experiment === o.experiment;
const isMini = line => [KIT, SET].includes(productId(line));

function cartCodes(cart) {
  return (cart?.discount_codes || []).map(entry => typeof entry === 'string'
    ? {code: entry.toUpperCase(), applicable: undefined}
    : {code: id(entry.code).toUpperCase(), applicable: entry.applicable});
}

function cleanKitProperties(properties) {
  return Object.fromEntries(Object.entries(properties || {})
    .filter(([key]) => !key.startsWith('_sl_trial_mini_')));
}

function setProperties(o, original, token) {
  // Only private trial tracking survives a Kit -> one-time Set conversion.
  // Public Kit marketing properties can imply the wrong subscription on the Set.
  const result = Object.fromEntries(Object.entries(original || {})
    .filter(([key]) => key.startsWith('_sl_') && !key.startsWith('_sl_trial_mini_')));
  return {...result, _sl_trial_mini_source:o.source,
    _sl_trial_mini_experiment:o.experiment, _sl_trial_mini_arm:'offer',
    _sl_trial_mini_operation:token};
}

function variants(product) { return Array.isArray(product?.variants) ? product.variants : []; }
function optionNames(product) {
  return (product?.options || []).map(value => typeof value === 'string' ? value : value?.name);
}
function variantOptions(variant) {
  return [variant?.option1, variant?.option2];
}
function catalogOK(product, expectedId) {
  const names = optionNames(product);
  return id(product?.id) === expectedId && names.length === 2 &&
    /^hair$/i.test(id(names[0])) && /^body$/i.test(id(names[1])) && variants(product).length > 0;
}
function scentsFor(line, catalog) {
  const found = variants(catalog).filter(v => id(v.id) === variantId(line));
  if (found.length !== 1 || variantOptions(found[0]).some(v => typeof v !== 'string' || !v)) return null;
  return variantOptions(found[0]);
}
function mappedVariant(catalog, scents) {
  const found = variants(catalog).filter(v => v.option1 === scents[0] && v.option2 === scents[1]);
  if (found.length !== 1) return {reason:'option-mapping'};
  if (found[0].available !== true) return {reason:'sold-out'};
  return {variant:found[0]};
}

function basic(cart, o) {
  const items = Array.isArray(cart?.items) ? cart.items.filter(line => quantity(line)) : [];
  const minis = items.filter(isMini);
  const kits = minis.filter(line => productId(line) === KIT);
  const sets = minis.filter(line => productId(line) === SET);
  const trial = kits.some(line => planId(line) === TRIAL_PLAN) || sets.some(line => owned(line,o));
  return {items,minis,kits,sets,trial,miniUnits:minis.reduce((n,line) => n + quantity(line),0)};
}

function validate(cart, expected, options = {}) {
  const o = config(options), a = basic(cart,o), issues = [];
  if (!cart || !Array.isArray(cart.items) || cart.items.some(line => !quantity(line))) issues.push('cart-unconfirmed');
  if (cart?.currency !== o.currency || o.marketCountry !== 'US') issues.push('market');
  if (a.kits.length !== 1 || a.kits.reduce((n,line)=>n+quantity(line),0) !== 1) issues.push('kit-count');
  else {
    if (planId(a.kits[0]) !== TRIAL_PLAN) issues.push('trial-plan');
    if (a.kits[0].final_line_price !== 0) issues.push('trial-price');
    if (expected?.kitVariantId && variantId(a.kits[0]) !== id(expected.kitVariantId)) issues.push('kit-variant');
  }
  const wantedSets = expected?.setQuantity ?? (a.miniUnits === 2 ? 1 : 0);
  if (a.sets.length !== wantedSets || a.sets.reduce((n,line)=>n+quantity(line),0) !== wantedSets) issues.push('set-count');
  if (wantedSets === 1 && a.sets.length === 1) {
    if (planId(a.sets[0])) issues.push('extra-selling-plan');
    if (!owned(a.sets[0],o)) issues.push('unowned-set');
    if (a.sets[0].final_line_price !== o.offerCents) issues.push('extra-price');
    if (expected?.setVariantId && variantId(a.sets[0]) !== id(expected.setVariantId)) issues.push('set-variant');
  }
  const codes = cartCodes(cart);
  const nativeShipping = codes.some(entry => entry.code === o.shippingCode && entry.applicable === true);
  if (wantedSets === 1 && a.items.every(isMini) && !nativeShipping) issues.push('shipping-unconfirmed');
  if (wantedSets === 0 && codes.some(entry => [o.shippingCode,o.offerCode,'TRIAL-EXTRA10'].includes(entry.code))) issues.push('stale-offer-code');
  if (a.miniUnits !== 1 + wantedSets) issues.push('mini-count');
  return {valid:issues.length === 0,issues,trial:a.trial,miniUnits:a.miniUnits,
    shippingUnlocked:issues.length === 0 && wantedSets === 1 && nativeShipping};
}

function plan(cart, catalogs = {}, options = {}) {
  const o = config(options), a = basic(cart,o);
  const result = {status:'none',reason:'not-trial',trial:a.trial,miniUnits:a.miniUnits,
    steps:[],expected:null,removeOfferCodes:false,requiresFreeKitCode:false};
  const blocked = reason => ({...result,status:'blocked',reason});
  if (!cart || !Array.isArray(cart.items) || cart.items.some(line => !quantity(line))) return blocked('cart-unconfirmed');
  if (!a.minis.length) return {...result,reason:'empty-or-unrelated',removeOfferCodes:cartCodes(cart)
    .some(entry => [o.shippingCode,o.offerCode,'TRIAL-EXTRA10'].includes(entry.code))};
  // Retail-only Set purchases have no trial ownership and must keep their agreed price.
  if (!a.trial && a.kits.length === 0) return result;
  if (cart.currency !== o.currency || o.marketCountry !== 'US') return blocked('market');
  if (a.kits.some(line => planId(line) !== TRIAL_PLAN) || a.sets.some(line => owned(line,o) && planId(line))) return blocked('unknown-plan');
  if (!a.trial) return result;
  if (a.sets.some(line => !owned(line,o))) return blocked('unowned-set');
  if (a.miniUnits > 2) return blocked('over-limit');
  if (a.miniUnits < 1) return blocked('mini-count');
  if (a.minis.some(line => typeof line.key !== 'string' || !line.key) ||
      new Set(a.minis.map(line => line.key)).size !== a.minis.length) return blocked('line-keys');
  if (!catalogOK(catalogs.kit,KIT) || !catalogOK(catalogs.set,SET)) return blocked('catalog');

  // Exactly one unit receives the trial role. Retain the first existing trial Kit
  // when present; otherwise the remaining owned Set becomes the trial Kit.
  const units = a.minis.flatMap(line => Array.from({length:quantity(line)},(_,unit)=>({line,unit})));
  const firstKit = units.find(unit => productId(unit.line) === KIT) || units[0];
  const second = units.find(unit => unit !== firstKit);
  const chosen = [{...firstKit,role:'kit'}, ...(second ? [{...second,role:'set'}] : [])];
  const targets = [];
  for (const unit of chosen) {
    const fromCatalog = productId(unit.line) === KIT ? catalogs.kit : catalogs.set;
    const toCatalog = unit.role === 'kit' ? catalogs.kit : catalogs.set;
    const scents = scentsFor(unit.line,fromCatalog);
    if (!scents) return blocked('source-variant');
    const mapped = mappedVariant(toCatalog,scents);
    if (!mapped.variant) return blocked(mapped.reason);
    const sameRole = productId(unit.line) === (unit.role === 'kit' ? KIT : SET);
    const token = `normalize-${id(cart.token).replace(/[^a-zA-Z0-9]/g,'').slice(0,24) || 'cart'}-${variantId(unit.line)}-${unit.unit}`;
    const properties = unit.role === 'kit' ? cleanKitProperties(unit.line.properties)
      : (sameRole ? {...unit.line.properties} : setProperties(o,unit.line.properties,token));
    targets.push({role:unit.role,productId:unit.role === 'kit' ? KIT : SET,
      variantId:id(mapped.variant.id),quantity:1,sellingPlanId:unit.role === 'kit' ? TRIAL_PLAN : null,
      properties,sourceKey:unit.line.key,sourceUnit:unit.unit,scents});
  }
  const steps = [];
  for (const line of a.minis) {
    const from = {key:line.key,quantity:quantity(line),variantId:variantId(line),productId:productId(line)};
    const assigned = targets.filter(target => target.sourceKey === line.key);
    // Assigned source units are processed together; no line is silently discarded.
    const retained = assigned.find(target => target.productId === productId(line) &&
      target.variantId === variantId(line) && id(target.sellingPlanId) === planId(line));
    if (retained) {
      if (quantity(line) !== 1) steps.push({type:'change',from,to:retained});
      for (const target of assigned.filter(target => target !== retained)) steps.push({type:'add',to:target});
    } else if (assigned.length) {
      steps.push({type:'replace',from,to:assigned[0]});
      for (const target of assigned.slice(1)) steps.push({type:'add',to:target});
    }
  }
  const expected = {kitVariantId:targets[0].variantId,setVariantId:targets[1]?.variantId || null,
    kitQuantity:1,setQuantity:second ? 1 : 0,totalCents:second ? o.offerCents : 0,
    requiresShippingDiscount:!!second && a.items.every(isMini),targets};
  const validation = validate(cart,expected,o);
  return {...result,status:steps.length || !validation.valid ? 'normalize' : 'ready',
    reason:steps.length ? 'roles' : validation.valid ? 'canonical' : 'pricing-or-discounts',
    steps,expected,removeOfferCodes:!second,requiresFreeKitCode:true,validation};
}

window.SwellTrialCartPolicy = {KIT,SET,TRIAL_PLAN,DEFAULTS,plan,planNormalization:plan,validate,
  cleanKitProperties,cartCodes};

})();
(function () {
  'use strict';
  const P = window.SwellTrialCartPolicy;
  const c = {marketCountry:window.SWELL_TRIAL_CART_COUNTRY,source:'trial-mini-cart-20-v1',experiment:'trial-mini-cart-20-v1'};
  const freeCode = 'HXK2RD8B3XCA', extraCodes = ['TRIAL-EXTRA1495','TRIAL-EXTRA-SHIP','TRIAL-EXTRA10'];
  const lockName = 'swell-trial-cart-integrity-v2';
  let busyDepth=0, pending = null, catalog = null, lastCart = null, editing = false, bypass = false;
  let feedback = '', failed = false, nativeDirty=false, checkoutResumePending=false;
  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  async function request(path, body) {
    const r = await fetch(root + path,{method:body?'POST':'GET',credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json',...(body?{'Content-Type':'application/json'}:{})},...(body?{body:JSON.stringify(body)}:{})});
    if (!r.ok) { const e = await r.json().catch(()=>({})); const error=new Error(e.description || 'Your cart could not update. Please try again.');error.definitive=[400,401,403,404,422].includes(r.status);throw error; }
    if(body)nativeDirty=true;
    return r.json();
  }
  async function withLock(fn) {
    if (!navigator.locks?.request) throw new Error('Please use a current browser to update this trial offer.');
    return navigator.locks.request(lockName,async()=>{
      setBusy(true);
      try{return await fn();}finally{setBusy(false);if(nativeDirty){nativeDirty=false;queueMicrotask(()=>{if(/\/cart\/?$/.test(location.pathname))location.reload();else window.SLIDECART_UPDATE?.();});}}
    });
  }
  async function catalogs() {
    if (!catalog) catalog = Promise.all([request('products/stem-sample.js'),request('products/mini-duo.js')]).then(([kit,set])=>({kit,set})).catch(e=>{catalog=null;throw e;});
    return catalog;
  }
  function codes(cart, pair, trial) {
    const values = window.SwellTrialMiniCore?.cartCodes(cart) || (cart.discount_codes||[]).map(x=>x.code);
    const out = values.filter(v=>!extraCodes.includes(v.toUpperCase()) && (trial || v.toUpperCase()!==freeCode));
    if (trial && !out.some(x=>x.toUpperCase()===freeCode)) out.push(freeCode);
    if (pair) {out.push('TRIAL-EXTRA1495');if ((cart.items||[]).every(i=>[P.KIT,P.SET].includes(String(i.product_id))))out.push('TRIAL-EXTRA-SHIP');}
    return [...new Set(out)].join(',');
  }
  function updateBusy() {
    const next=busyDepth>0 && needsCoordination(lastCart);
    if(next===editing)return;
    editing=next;
    document.documentElement.toggleAttribute('data-swell-trial-editing',editing);
    document.querySelectorAll('#slidecart-checkout-form [name="checkout"],button[name="checkout"][form="cart"]').forEach(b=>b.setAttribute('aria-busy',String(editing)));
  }
  function setBusy(value) {
    busyDepth=Math.max(0,busyDepth+(value?1:-1));updateBusy();
  }
  function remember(cart) {lastCart=cart;updateBusy();return cart;}
  function notice(message, error=false) {
    feedback=message;failed=error;
    const form=document.querySelector('#slidecart-checkout-form');
    let n=document.getElementById('swell-trial-cart-feedback');
    if (!n && form) {n=document.createElement('p');n.id='swell-trial-cart-feedback';n.setAttribute('role','status');form.before(n);}
    if(n){n.hidden=!message;if(n.textContent!==message)n.textContent=message;n.dataset.error=String(error);}
  }
  function owned(i) {return String(i.product_id)==='10191250194745' && i.properties?._sl_trial_mini_source===c.source && i.properties?._sl_trial_mini_experiment===c.experiment;}
  function trialItem(i) {return String(i.product_id)==='9953072251193' && String(i.selling_plan_allocation?.selling_plan?.id)==='692131856697';}
  function needsCoordination(cart) {
    return !!cart?.items?.some(i=>trialItem(i)||owned(i));
  }
  async function passthrough(cart) {
    sessionStorage.removeItem('swell:trial-cart-integrity:journal');notice('',false);
    const stale=(cart.discount_codes||[]).some(x=>[...extraCodes,freeCode].includes(String(x.code).toUpperCase()));
    if(stale){cart=await request('cart/update.js',{discount:codes(cart,false,false)});
      nativeDirty=false;queueMicrotask(()=>refreshNative());}
    return remember(cart);
  }
  function findSource(cart, from) {
    const matches=cart.items.filter(i=>String(i.variant_id)===String(from.variantId) && String(i.product_id)===String(from.productId) && String(i.selling_plan_allocation?.selling_plan?.id||'')===String(from.sellingPlanId||'') && JSON.stringify(i.properties||{})===JSON.stringify(from.properties||{}));
    const exact=matches.find(i=>i.key===from.key);
    if(exact)return exact;
    if(matches.length>1)throw new Error('Your cart changed in another tab. Refresh before continuing.');
    return matches[0];
  }
  function operationId() {return crypto.randomUUID();}
  async function createTarget(to, op) {
    const properties={...(to.properties||{}),_sl_trial_cart_repair:op};
    if(to.role==='set') Object.assign(properties,{_sl_trial_mini_source:c.source,_sl_trial_mini_experiment:c.experiment,_sl_trial_mini_arm:'offer',_sl_trial_mini_operation:op});
    const line={id:to.variantId,quantity:to.quantity||1,properties,...(to.sellingPlanId?{selling_plan:to.sellingPlanId}:{})};
    let err;
    try{await request('cart/add.js',{items:[line]});}catch(e){err=e;}
    const cart=await request('cart.js');
    const matches=cart.items.filter(i=>i.properties?._sl_trial_cart_repair===op);
    if(matches.length!==1 || matches[0].quantity!==(to.quantity||1))throw err||new Error('Your cart update is not confirmed. Please refresh before checkout.');
    if(String(matches[0].selling_plan_allocation?.selling_plan?.id||'')!==String(to.sellingPlanId||''))throw new Error('Your trial selection could not be confirmed. Please refresh before checkout.');
    return cart;
  }
  function targetMatches(line,to){return line && String(line.variant_id)===String(to.variantId) && String(line.product_id)===(to.role==='kit'?'9953072251193':'10191250194745') && line.quantity===(to.quantity||1) && String(line.selling_plan_allocation?.selling_plan?.id||'')===String(to.sellingPlanId||'');}
  async function applyPlan(cart, plan) {
    const key='swell:trial-cart-integrity:journal';
    let journal;
    try{journal=JSON.parse(sessionStorage.getItem(key));}catch(_){}
    if(journal && journal.token!==cart.token){sessionStorage.removeItem(key);journal=null;}
    if(!journal){
      journal={token:cart.token,phase:'prepare',expected:plan.expected,entries:(plan.steps||[]).map(step=>({step:{...step,...(step.from?{from:{...step.from,properties:cart.items.find(i=>i.key===step.from.key)?.properties||{},sellingPlanId:String(cart.items.find(i=>i.key===step.from.key)?.selling_plan_allocation?.selling_plan?.id||'')}}:{})},op:operationId(),attempted:false}))};
      sessionStorage.setItem(key,JSON.stringify(journal));
    }
    function save(){sessionStorage.setItem(key,JSON.stringify(journal));}
    for(const entry of journal.entries){
      if(!['replace','add'].includes(entry.step.type))continue;
      const existing=cart.items.filter(i=>i.properties?._sl_trial_cart_repair===entry.op);
      if(existing.length>1 || existing.length===1 && !targetMatches(existing[0],entry.step.to))throw new Error('Your cart changed in another tab. Refresh before checkout.');
      if(!existing.length){
        if(entry.attempted)throw new Error('We couldn’t confirm this update. Remove the trial items, then add your trial again.');
        entry.attempted=true;save();
        try{cart=await createTarget(entry.step.to,entry.op);}catch(e){if(e.definitive){entry.attempted=false;save();}throw e;}
      }
    }
    cart=await request('cart.js');
    for(const entry of journal.entries.filter(e=>['replace','add'].includes(e.step.type))){const matches=cart.items.filter(i=>i.properties?._sl_trial_cart_repair===entry.op);if(matches.length!==1 || !targetMatches(matches[0],entry.step.to))throw new Error('Your cart changed in another tab. Refresh before checkout.');}
    journal.phase='commit';save();
    for(const entry of journal.entries){
      const step=entry.step;
      if(!['replace','change'].includes(step.type))continue;
      cart=await request('cart.js');
      const source=findSource(cart,step.from);
      if(source){
        const quantity=step.type==='change'?step.to.quantity:0;
        if(source.quantity!==step.from.quantity && source.quantity!==quantity)throw new Error('Your quantity changed in another tab. Refresh before continuing.');
        if(source.quantity!==quantity)await request('cart/change.js',{id:source.key,quantity});
      }
    }
    cart=await request('cart.js');
    cart=await request('cart/update.js',{discount:codes(cart,journal.expected.setQuantity===1,true)});
    if(!P.validate(cart,journal.expected,c).valid)throw new Error('Your trial update is not confirmed. Please refresh before checkout.');
    sessionStorage.removeItem(key);return cart;
  }
  async function reconcile() {
    let cart=remember(await request('cart.js'));
    if(!needsCoordination(cart))return passthrough(cart);
    const cat=await catalogs();let plan=P.plan(cart,cat,c);
    let journal;try{journal=JSON.parse(sessionStorage.getItem('swell:trial-cart-integrity:journal'));}catch(_){}
    if(journal?.token===cart.token){setBusy(true);try{cart=await applyPlan(cart,plan);plan=P.plan(cart,cat,c);}finally{setBusy(false);}}
    if(plan.status==='blocked'){const messages={'sold-out':'This scent pair is unavailable for the trial. Choose another scent or remove it to continue.','over-limit':'Your trial offer includes up to two Mini Duos. Remove an extra Mini Duo to continue.','mixed-cart':'Keep the trial offer in a separate order. Remove the other product or the trial items to continue.','unknown-plan':'Review the Mini Duo purchase options before checkout.','unowned-set':'Remove the extra retail Mini Duo, then add it through the trial offer.'};notice(messages[plan.reason]||'Your trial selection needs a refresh before checkout.',true);return remember(cart);}
    if(plan.status==='normalize'){
      setBusy(true);
      try{cart=await applyPlan(cart,plan);plan=P.plan(cart,cat,c);}
      finally{setBusy(false);}
    }
    if(plan.expected){
      const desired=codes(cart,plan.expected.setQuantity===1,true);
      const check=P.validate(cart,plan.expected,c);
      const current=(cart.discount_codes||[]).map(x=>x.code.toUpperCase());
      const desiredUpper=desired.split(',').map(x=>x.toUpperCase());
      if(!check.valid || current.length!==desiredUpper.length || desiredUpper.some(x=>!current.includes(x)))cart=await request('cart/update.js',{discount:desired});
      const final=P.validate(cart,plan.expected,c);
      if(!final.valid)throw new Error('Your trial price could not be confirmed. Refresh your cart or choose another scent.');
      notice('',false);
    }
    return remember(cart);
  }
  function read(options={}){
    if(options.insideLock)return reconcile();
    if(pending)return pending;
    // Read-only ordinary carts do not require the trial mutation lock.
    pending=(async()=>{
      const cart=remember(await request('cart.js'));
      if(!needsCoordination(cart))return passthrough(cart);
      return withLock(reconcile);
    })().catch(e=>{if(needsCoordination(lastCart))notice(e.message,true);throw e;}).finally(()=>{pending=null;});return pending;
  }
  async function refreshNative(){
    if(/\/cart\/?$/.test(location.pathname))location.reload();
    else if(typeof window.SLIDECART_UPDATE==='function')window.SLIDECART_UPDATE();
  }
  async function sync(){try{await read();await window.SwellTrialMiniOffer?.refresh();await refreshNative();}catch(e){notice(e.message,true);}}
  let quantityAddPending=false,quantityAddPromise=null;
  function addSecond(){
    if(quantityAddPromise)return quantityAddPromise;
    quantityAddPending=true;
    quantityAddPromise=withLock(async()=>{
        const cart=await read({insideLock:true});
        if(cart.items.filter(trialItem).reduce((n,i)=>n+i.quantity,0)!==1||cart.items.some(owned))return;
        const item=cart.items.find(trialItem),cat=await catalogs();
        if(!item||!P.validate(cart,{setQuantity:0},c).valid)throw new Error('Refresh your cart before adding an extra item.');
        const kv=cat.kit.variants.find(v=>String(v.id)===String(item.variant_id));
        const sv=cat.set.variants.find(v=>v.option1===kv?.option1&&v.option2===kv?.option2&&v.available);
        if(!sv)throw new Error('This extra scent pair is sold out. Your original free trial is unchanged.');
        await createTarget({role:'set',variantId:String(sv.id),quantity:1,sellingPlanId:null,
          properties:Object.fromEntries(Object.entries(item.properties||{}).filter(([key])=>key.startsWith('_sl_')&&!key.startsWith('_sl_trial_mini_')))},operationId());
        const confirmed=await read({insideLock:true});
        if(!P.validate(confirmed,{kitVariantId:String(item.variant_id),setVariantId:String(sv.id),setQuantity:1},c).valid)
          throw new Error('Your extra item could not be confirmed. Refresh your cart before checkout.');
        notice('',false);
      }).finally(()=>{quantityAddPending=false;quantityAddPromise=null;renderControls();});
    return quantityAddPromise;
  }
  function rowItem(target){const row=target.closest?.('#slidecarthq .item[data-line-item-key]');return row&&lastCart?.items.find(i=>i.key===row.getAttribute('data-line-item-key'));}
  function total(){return(lastCart?.items||[]).filter(i=>trialItem(i)||owned(i)).reduce((n,i)=>n+i.quantity,0);}
  function stop(e){e.preventDefault();e.stopImmediatePropagation();}
  window.addEventListener('click',event=>{
    if(bypass)return;
    const uniformCheckout=event.target.closest?.('#slidecart-checkout-form [name="checkout"],button[name="checkout"][form="cart"]');
    if(uniformCheckout && window.SwellUniformPricing?.enabled===true){stop(event);window.SwellUniformPricing.checkout().catch(e=>notice(e.message,true));return;}
    const editingItem=rowItem(event.target);
    if(editing && ((editingItem&&(trialItem(editingItem)||owned(editingItem))) || event.target.closest?.('[data-sl-mini-action]'))){stop(event);return;}
    const plus=event.target.closest?.('#slidecarthq .quantity-selector button[aria-label^="Increase quantity"]');
    const item=plus&&rowItem(plus);
    if(item&&(trialItem(item)||owned(item))){stop(event);if(total()>=2)notice('Your trial offer includes up to two Mini Duos.');else addSecond().then(sync).catch(e=>notice(e.message,true));return;}
    if(!needsCoordination(lastCart))return;
    const button=event.target.closest?.('#slidecart-checkout-form [name="checkout"],button[name="checkout"][form="cart"]');
    if(button&&(editing||failed||pending||quantityAddPending)){
      stop(event);if(checkoutResumePending)return;checkoutResumePending=true;
      Promise.resolve(quantityAddPromise).then(()=>read()).then(()=>{
        if(editing||failed)return;
        location.assign(root+'checkout');
      }).catch(e=>notice(e.message,true)).finally(()=>{checkoutResumePending=false;});
    }
  },true);
  let claimPending=false,nativeClaim=false;
  document.addEventListener('click',event=>{
    const button=event.target.closest?.('button[data-rid="d033806f-7ff8-4044-8669-27d0a8a8904d"][data-replo-add-product-variant-to-cart]');
    if(!button||nativeClaim||button.disabled||button.getAttribute('aria-disabled')==='true'||!/^\/pages\/trialtop5\/?$/.test(location.pathname))return;
    stop(event);if(claimPending)return;claimPending=true;
    withLock(async()=>{
      const cart=await read({insideLock:true});
      if(cart.items.some(i=>trialItem(i)||owned(i))){window.SLIDECART_OPEN?.();return;}
      nativeClaim=true;try{button.click();}finally{nativeClaim=false;}
      for(let n=0;n<20;n++){await new Promise(r=>setTimeout(r,350));const latest=await request('cart.js');if(latest.items.some(trialItem)){await read({insideLock:true});return;}}
    }).catch(e=>notice(e.message,true)).finally(()=>{claimPending=false;});
  },true);
  document.addEventListener('change',event=>{
    const input=event.target.closest?.('#slidecarthq .quantity-selector input'),item=input&&rowItem(input);
    if(!item||(!trialItem(item)&&!owned(item)))return;
    const n=Number(input.value);
    if(n>1){stop(event);input.value=String(item.quantity);if(total()<2)addSecond().then(sync).catch(e=>notice(e.message,true));else notice('Your trial offer includes up to two Mini Duos.');}
  },true);
  const rewardCopies=new Map();
  function renderTrialProgress(active,unlocked,title){
    for(const [native,entry] of rewardCopies){if(!native.isConnected){entry.clone.remove();rewardCopies.delete(native);}}
    document.querySelectorAll('#slidecarthq .rewards:not([data-swell-trial-progress]):not([data-swell-cart-rewards])').forEach(native=>{
      let entry=rewardCopies.get(native);
      if(active){
        if(!entry){const clone=native.cloneNode(true);clone.setAttribute('data-swell-trial-progress','');native.setAttribute('data-swell-native-progress','');native.after(clone);entry={clone,source:null};rewardCopies.set(native,entry);}
        const source=native.innerHTML;
        if(entry.source!==source){entry.clone.innerHTML=source;entry.clone.querySelectorAll('[id]').forEach(n=>n.removeAttribute('id'));entry.source=source;}
        entry.clone.hidden=false;
        entry.clone.querySelectorAll('p').forEach(n=>{if(n.textContent!==title)n.textContent=title;});
        entry.clone.querySelectorAll('.rewards-tiers-labels-item-label').forEach(n=>{const text=n.textContent.replace(/Priority\s+/gi,'');if(n.textContent!==text)n.textContent=text;});
        entry.clone.querySelectorAll('[role="progressbar"]').forEach(n=>{n.setAttribute('aria-valuenow',unlocked?'100':'50');n.setAttribute('aria-valuetext',title);});
      }else if(entry){entry.clone.hidden=true;}
    });
  }
  function renderControls(){
    const isTrial=needsCoordination(lastCart);
    const trialDisplay=isTrial && /^\/pages\/trialtop5\/?$/.test(location.pathname) &&
      lastCart.items.filter(i=>String(i.variant_id)!=='67679733973305').every(i=>trialItem(i)||owned(i));
    const unlocked=trialDisplay && P.validate(lastCart,null,c).shippingUnlocked && !editing;
    document.body?.classList.toggle('sl-trial-cart',trialDisplay);
    document.body?.classList.toggle('sl-trial-shipping-unlocked',unlocked);
    renderTrialProgress(trialDisplay,unlocked,editing?'Updating your cart…':unlocked?'FREE shipping unlocked!':'Add another item to unlock FREE shipping');
    document.querySelectorAll('#slidecarthq .item[data-line-item-key]').forEach(row=>{
      const item=lastCart?.items.find(i=>i.key===row.getAttribute('data-line-item-key'));if(!item||(!trialItem(item)&&!owned(item)))return;
      const plus=row.querySelector('button[aria-label^="Increase quantity"]');if(plus){plus.disabled=editing||quantityAddPending||total()>=2;plus.setAttribute('aria-disabled',String(plus.disabled));}
      const input=row.querySelector('.quantity-selector input');if(input){input.readOnly=false;input.removeAttribute('aria-readonly');input.max='2';}
      if(trialItem(item)){
        let terms=row.querySelector('[data-swell-trial-terms]');if(!terms){terms=document.createElement('p');terms.dataset.swellTrialTerms='';terms.textContent='Free trial';row.querySelector('.title')?.after(terms);}
      }
    });
    if(feedback)notice(feedback,failed);
  }
  new MutationObserver(records=>{if(records.some(r=>r.addedNodes.length))renderControls();}).observe(document.documentElement,{childList:true,subtree:true});
  ['SLIDECART_UPDATED','SLIDECART_OPENED','SLIDECART_ADDED_TO_CART'].forEach(name=>{const prev=window[name];window[name]=function(...args){try{return prev?.apply(this,args);}finally{queueMicrotask(()=>read().then(renderControls).catch(()=>{}));}};});
  window.SwellTrialCartIntegrity={read,withLock,catalogs,renderControls,getStatus:()=>({editing,failed,feedback}),sync};
})();


